import React from "react";
import { SelectChangeEvent } from "@mui/material";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSFormControl from "../../system-design/DSFormControl";
import DSChip from "../../system-design/DSChip";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import { DeviceChangeLogFields } from "../hooks/useClockChangeLogs";

interface ClockFieldSelectProps {
  selectedFields: Array<keyof DeviceChangeLogFields>;
  onFieldsChange: (fields: Array<keyof DeviceChangeLogFields>) => void;
  placeholder?: string;
  helperText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
}

const AVAILABLE_FIELDS: Array<{
  value: keyof DeviceChangeLogFields;
  label: string;
}> = [
  { value: "favorites", label: "Favorites" },
  { value: "feeds", label: "Feeds" },
  { value: "hideDev", label: "Hide Dev" },
  { value: "registerProduct", label: "Register Product" },
  { value: "timeOffset", label: "Time Offset" },
  { value: "timeFormat", label: "Time Format" },
  { value: "klydo", label: "Klydo" },
  { value: "brightness", label: "Brightness" },
  { value: "sortDown", label: "Sort Down" },
  { value: "chimes", label: "Chimes" },
  { value: "chimesVol", label: "Chimes Volume" },
  { value: "chimeMode", label: "Chime Mode" },
  { value: "ticks", label: "Ticks" },
  { value: "ticksVol", label: "Ticks Volume" },
  { value: "quietTimesOn", label: "Quiet Times On" },
  { value: "quietTimes", label: "Quiet Times" },
  { value: "nightMode", label: "Night Mode" },
  { value: "nightModeAppearance", label: "Night Mode Appearance" },
  { value: "nightModeTimes", label: "Night Mode Times" },
  { value: "show", label: "Show" },
  { value: "showPSF", label: "Show PSF" },
  { value: "show13Plus", label: "Show 13+" },
  { value: "showDials", label: "Show Dials" },
  { value: "freshHold", label: "Fresh Hold" },
  { value: "hourlyStrikes", label: "Hourly Strikes" },
  { value: "hardVersion", label: "Hard Version" },
  { value: "machineCollections", label: "Machine Collections" },
  { value: "curCollection", label: "Current Collection" },
  { value: "curCollections", label: "Current Collections" },
  { value: "curFavs", label: "Current Favorites" },
  { value: "curFeed", label: "Current Feed" },
  { value: "mode", label: "Mode" },
  { value: "beta", label: "Beta" },
  { value: "isRetail", label: "Is Retail" },
  { value: "klydosSound", label: "Klydos Sound" },
  { value: "masterSoundMixer", label: "Master Sound Mixer" },
  { value: "playerVolume", label: "Player Volume" },
  { value: "screenState", label: "Screen State" },
  { value: "tag", label: "Tag" },
  { value: "version", label: "Version" },
  { value: "hiddenKlydos", label: "Hidden Klydos" },
  { value: "isArtist", label: "Is Artist" },
  { value: "pendingKlydos", label: "Pending Klydos" },
  { value: "stayInFavorits", label: "Stay In Favorites" },
];

const ClockFieldSelect: React.FC<ClockFieldSelectProps> = ({
  selectedFields,
  onFieldsChange,
  placeholder = "Select fields...",
  helperText = "Choose which fields to display",
  fullWidth = true,
  disabled = false,
}) => {
  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    onFieldsChange(
      typeof value === "string"
        ? (value.split(",") as Array<keyof DeviceChangeLogFields>)
        : (value as Array<keyof DeviceChangeLogFields>),
    );
  };

  return (
    <>
      <DSFormControl fullWidth={fullWidth} variant="outlined">
        <DSSelect
          multiple
          value={selectedFields as string[]}
          onChange={handleChange}
          displayEmpty
          disabled={disabled}
          renderValue={(selected) => (
            <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {(selected as string[])?.length === 0 ? (
                <em style={{ color: "#999" }}>{placeholder}</em>
              ) : (
                (selected as string[])?.map((value) => {
                  const field = AVAILABLE_FIELDS.find((f) => f.value === value);
                  return (
                    <DSChip
                      key={value}
                      label={field?.label || value}
                      size="small"
                    />
                  );
                })
              )}
            </DSBox>
          )}
        >
          {AVAILABLE_FIELDS.map((field) => (
            <DSMenuItem key={field.value} value={field.value}>
              {field.label}
            </DSMenuItem>
          ))}
        </DSSelect>
      </DSFormControl>
      {helperText && (
        <DSTypography
          variant="caption"
          color="textSecondary"
          sx={{ mt: 0.5, ml: 1.75, display: "block" }}
        >
          {helperText}
        </DSTypography>
      )}
    </>
  );
};

export default ClockFieldSelect;
export { AVAILABLE_FIELDS };
